import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
    CheckCircle2, Clock, Loader2, Sparkles, Tag, MailCheck, AlertCircle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtEur = (v) =>
    v == null
        ? "—"
        : new Intl.NumberFormat("it-IT", {
              style: "currency", currency: "EUR",
              minimumFractionDigits: v % 1 === 0 ? 0 : 2,
              maximumFractionDigits: 2,
          }).format(v);

const SUB_FLOW_LABELS = {
    ftv_promo: "Impianto Fotovoltaico",
    st_promo: "Solare Termico",
    clima_promo: "Climatizzatore",
    pdc_promo: "Pompa di Calore",
};

export default function PublicQuotePage() {
    const { token } = useParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [quote, setQuote] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [confirmed, setConfirmed] = useState(false);
    const [note, setNote] = useState("");

    useEffect(() => {
        let cancelled = false;
        axios.get(`${API}/p/${token}`)
            .then(({ data }) => {
                if (cancelled) return;
                setQuote(data.quote);
                setConfirmed(!!data.quote.confirmed_online);
            })
            .catch((e) => {
                if (cancelled) return;
                setError(e.response?.status === 404
                    ? "Preventivo non trovato o link scaduto."
                    : "Errore di caricamento. Riprova più tardi.");
            })
            .finally(() => !cancelled && setLoading(false));
        return () => { cancelled = true; };
    }, [token]);

    const submit = async () => {
        setSubmitting(true);
        try {
            await axios.post(`${API}/p/${token}/confirm`, {
                note: note.trim() || null,
            });
            setConfirmed(true);
        } catch (e) {
            setError(e.response?.data?.detail || "Errore invio conferma.");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <Shell>
                <div className="text-center py-20 text-[#91C6C0]">
                    <Loader2 className="w-8 h-8 animate-spin mx-auto" />
                    <div className="mt-3">Caricamento preventivo…</div>
                </div>
            </Shell>
        );
    }

    if (error) {
        return (
            <Shell>
                <div className="max-w-md mx-auto bg-white rounded-2xl shadow-2xl p-8 text-center">
                    <AlertCircle className="w-12 h-12 mx-auto text-red-500" />
                    <h2 className="mt-3 text-xl font-black text-[#0C4650]">Ops!</h2>
                    <p className="mt-2 text-gray-600">{error}</p>
                </div>
            </Shell>
        );
    }

    return (
        <Shell>
            <div className="max-w-2xl mx-auto" data-testid="public-quote-page">
                <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
                    <div className="bg-gradient-to-br from-[#0C4650] via-[#0a3a42] to-[#06252b] p-8 sm:p-10 text-white">
                        <div className="inline-flex items-center gap-2 bg-[#C7FF2C]/20 text-[#C7FF2C] px-3 py-1 rounded-full text-[11px] font-black tracking-widest">
                            <Sparkles className="w-3 h-3" /> SCINTILLA HUB · PROPOSTA
                        </div>
                        <h1 className="mt-4 text-3xl sm:text-4xl font-black leading-tight">
                            Ciao {quote.client_name},
                        </h1>
                        <p className="mt-2 text-[#91C6C0]">
                            ecco il riepilogo della proposta che ti è stata preparata da{" "}
                            <strong className="text-white">{quote.agent_name}</strong>.
                        </p>
                    </div>

                    <div className="p-8 sm:p-10 space-y-6">
                        <div>
                            <div className="text-[10px] tracking-[0.3em] font-bold text-[#898A8D]">
                                {SUB_FLOW_LABELS[quote.sub_flow] || "PROPOSTA TECNICA"}
                            </div>
                            <div className="mt-2 text-2xl font-black text-[#0C4650] leading-tight">
                                {quote.system_label}
                            </div>
                        </div>

                        <div className="grid sm:grid-cols-2 gap-3">
                            <div className="rounded-2xl bg-[#C7FF2C]/30 border-2 border-[#C7FF2C] p-5">
                                <div className="text-[10px] tracking-widest font-bold text-[#0C4650]">
                                    PREZZO CHIAVI IN MANO
                                </div>
                                <div className="mt-1 text-3xl font-black text-[#0C4650]">
                                    {fmtEur(quote.final_price)}
                                </div>
                                <div className="text-[11px] text-gray-600 mt-1">
                                    IVA inclusa
                                </div>
                            </div>
                            {quote.monthly_payment && (
                                <div className="rounded-2xl bg-[#0C4650] text-white p-5">
                                    <div className="text-[10px] tracking-widest font-bold text-[#C7FF2C]">
                                        OPPURE IN {quote.months} RATE
                                    </div>
                                    <div className="mt-1 text-3xl font-black">
                                        {fmtEur(quote.monthly_payment)}/mese
                                    </div>
                                    <div className="text-[11px] text-[#91C6C0] mt-1">
                                        TAEG ind. 7,2% · soggetto ad approvazione
                                    </div>
                                </div>
                            )}
                        </div>

                        {confirmed ? (
                            <div className="rounded-2xl bg-emerald-50 border-2 border-emerald-300 p-6 text-center"
                                 data-testid="public-confirmed-state">
                                <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500" />
                                <h3 className="mt-2 text-xl font-black text-emerald-700">
                                    Conferma ricevuta!
                                </h3>
                                <p className="mt-1 text-sm text-emerald-700/90">
                                    Grazie. {quote.agent_name} ti contatterà a breve per
                                    completare l'iter contrattuale.
                                </p>
                                <div className="mt-3 inline-flex items-center gap-2 text-xs font-bold text-emerald-700">
                                    <MailCheck className="w-3.5 h-3.5" />
                                    Notifica inviata via email all'agente
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <div>
                                    <label className="text-[11px] tracking-widest font-bold text-[#898A8D]">
                                        VUOI LASCIARE UN MESSAGGIO? (opzionale)
                                    </label>
                                    <textarea
                                        rows={3}
                                        value={note}
                                        onChange={(e) => setNote(e.target.value)}
                                        placeholder="Es: Confermo, mi può chiamare nel pomeriggio…"
                                        data-testid="public-confirm-note"
                                        className="mt-1 w-full rounded-xl border-2 border-gray-200 p-3 text-sm focus:border-[#0C4650] outline-none resize-none"
                                    />
                                </div>
                                <button
                                    onClick={submit}
                                    disabled={submitting}
                                    data-testid="public-confirm-btn"
                                    className="w-full bg-[#C7FF2C] text-[#0C4650] font-black px-6 py-4 rounded-xl text-base hover:-translate-y-0.5 transition-all disabled:opacity-50 inline-flex items-center justify-center gap-2"
                                >
                                    {submitting ? (
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                    ) : (
                                        <>
                                            <CheckCircle2 className="w-5 h-5" />
                                            Confermo questa proposta
                                        </>
                                    )}
                                </button>
                                <div className="text-center text-[11px] text-gray-500">
                                    <Clock className="w-3 h-3 inline-block mr-1" />
                                    Prezzo bloccato per 30 giorni dalla data di emissione
                                </div>
                            </div>
                        )}

                        <div className="border-t border-gray-100 pt-5 text-center">
                            <div className="text-[10px] tracking-widest font-bold text-[#898A8D]">
                                IL TUO REFERENTE
                            </div>
                            <div className="mt-1 font-black text-[#0C4650]">{quote.agent_name}</div>
                            <a href={`mailto:${quote.agent_email}`} className="text-sm text-[#0C4650] hover:underline">
                                {quote.agent_email}
                            </a>
                        </div>
                    </div>
                </div>

                <div className="mt-6 text-center text-[11px] text-white/70">
                    <Tag className="w-3 h-3 inline-block mr-1" />
                    Powered by Scintilla Hub · Apertura tracciata
                </div>
            </div>
        </Shell>
    );
}

const Shell = ({ children }) => (
    <div className="min-h-screen w-full bg-gradient-to-br from-[#0C4650] via-[#0a3a42] to-[#06252b] p-4 sm:p-8 flex items-center"
         data-testid="public-quote-shell">
        <div className="w-full">{children}</div>
    </div>
);
